/**
 * Utilities for download feature: filename sanitization, page selection validation, and safe dimensions.
 * Used by DownloadDialog for robust export behavior and edge-case handling.
 */

/** Characters not allowed in filenames (cross-platform safe) */
const INVALID_FILENAME_CHARS = /[<>:"/\\|?*\x00-\x1f]/g;

/** Default max dimension for html2canvas (avoids browser memory/timeout issues) */
export const MAX_CANVAS_DIMENSION = 8000;

/** Default scale used by html2canvas in DownloadDialog */
export const EXPORT_SCALE = 2;

/**
 * Sanitize a filename: remove invalid chars and trim. Ensures a safe basename.
 * @param {string} name - Proposed filename (without extension)
 * @param {string} ext - Extension including dot (e.g. '.png')
 * @returns {string} Safe filename with extension
 */
export function sanitizeFilename(name, ext = '') {
  if (name == null || typeof name !== 'string') return `design${ext}`;
  const base = name.replace(INVALID_FILENAME_CHARS, '').trim() || 'design';
  const withoutExt = base.replace(/\.[^.]+$/, '');
  return `${withoutExt || 'design'}${ext}`;
}

/**
 * Get pages to download from full pages array and selected page ids.
 * Filters to existing pages only (handles deleted/stale ids).
 * @param {Array<{ id: string }>} pages - All pages
 * @param {string[]} selectedPageIds - Selected page ids
 * @returns {Array} Subset of pages in order of selection
 */
export function getPagesToDownload(pages, selectedPageIds) {
  if (!Array.isArray(pages) || !Array.isArray(selectedPageIds)) return [];
  const idSet = new Set(selectedPageIds);
  return pages.filter((p) => p && idSet.has(p.id));
}

/**
 * Clamp and validate page range so from <= to and both within [1, totalPages].
 * @param {number} from - Start page (1-based)
 * @param {number} to - End page (1-based)
 * @param {number} totalPages - Total number of pages
 * @returns {{ from: number, to: number }}
 */
export function validatePageRange(from, to, totalPages) {
  const total = Math.max(0, Math.floor(totalPages));
  let f = Math.max(1, Math.min(total, Math.floor(Number(from)) || 1));
  let t = Math.max(1, Math.min(total, Math.floor(Number(to)) || 1));
  if (f > t) [f, t] = [t, f];
  return { from: f, to: t };
}

/**
 * Check if canvas dimensions are within safe limits for html2canvas.
 * @param {number} width
 * @param {number} height
 * @param {number} maxDim - Max allowed for a single side
 * @returns {{ ok: boolean, scale?: number }} If not ok, scale suggests downscale factor to fit
 */
export function getSafeExportDimensions(width, height, maxDim = MAX_CANVAS_DIMENSION) {
  const w = Math.max(0, Math.floor(Number(width)));
  const h = Math.max(0, Math.floor(Number(height)));
  if (w <= 0 || h <= 0) return { ok: false };
  if (w <= maxDim && h <= maxDim) return { ok: true };
  const scale = Math.min(maxDim / w, maxDim / h, 1);
  return { ok: false, scale };
}

/**
 * Build export filename for a single image or multi-page PDF.
 * @param {string} baseName - Design/page name
 * @param {string} format - 'png' | 'jpeg' | 'pdf'
 * @param {boolean} multiPage - Whether multiple pages
 * @param {number} [pageIndex] - 1-based page index when multiPage
 * @returns {string}
 */
export function getDownloadFilename(baseName, format, multiPage = false, pageIndex = 1) {
  const ext = format === 'pdf' ? '.pdf' : format === 'jpeg' ? '.jpg' : '.png';
  const name = baseName || 'design';
  if (multiPage && format !== 'pdf') {
    return sanitizeFilename(`${name}-page${pageIndex}`, ext);
  }
  if (multiPage && format === 'pdf') {
    return sanitizeFilename(`${name}-${pageIndex}pages`, ext);
  }
  return sanitizeFilename(name, ext);
}
